import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ChevronRight } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { useConfigStore } from '@/stores/useConfigStore'
import { publicNetworks, testNetworks, isTestNetwork } from '@/services/blockchain/networks'
import type { NetworkNode } from '@/types'

const SLIPPAGE_OPTIONS = [
  { label: '1%', value: 1 },
  { label: '5%', value: 5 },
  { label: '10%', value: 10 },
  { label: '15%', value: 15 },
]

const IS_DEV = import.meta.env.DEV

async function measureLatency(rpc: string): Promise<number | null> {
  const start = performance.now()
  try {
    await fetch(rpc + '/v1/chain/get_info', { method: 'POST', signal: AbortSignal.timeout(5000) })
    return Math.round(performance.now() - start)
  } catch {
    return null
  }
}

export function SettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const { slippageTolerance, isMultipath, rpcUrl, setSlippageTolerance, setIsMultipath, setRpcUrl, setIsDev } = useConfigStore()

  const [selectedSlippage, setSelectedSlippage] = useState(slippageTolerance)
  const [customValue, setCustomValue] = useState('')
  const [isCustom, setIsCustom] = useState(false)
  const [multipath, setMultipath] = useState(isMultipath)
  const [showNodes, setShowNodes] = useState(false)
  const [selectedRpc, setSelectedRpc] = useState(rpcUrl)
  const [latencies, setLatencies] = useState<Record<string, number | null>>({})

  useEffect(() => {
    if (open) {
      setSelectedSlippage(slippageTolerance)
      setMultipath(isMultipath)
      setSelectedRpc(rpcUrl)
      setShowNodes(false)
      setLatencies({})
      const preset = SLIPPAGE_OPTIONS.find((o) => o.value === slippageTolerance)
      setIsCustom(!preset)
      if (!preset) setCustomValue(String(slippageTolerance))
    }
  }, [open, slippageTolerance, isMultipath, rpcUrl])

  const pingNodes = useCallback(() => {
    const allNodes = [...publicNetworks, ...(IS_DEV ? testNetworks : [])]
    allNodes.forEach((node) => {
      measureLatency(node.rpc).then((ms) => {
        setLatencies((prev) => ({ ...prev, [node.rpc]: ms }))
      })
    })
  }, [])

  useEffect(() => {
    if (showNodes) pingNodes()
  }, [showNodes, pingNodes])

  const handleClose = () => {
    const finalValue = isCustom ? parseInt(customValue) || selectedSlippage : selectedSlippage
    if (finalValue <= 0 || finalValue >= 100) {
      toast.error(t('public.slippage') + ': 1-99')
      return
    }
    const changed = finalValue !== slippageTolerance || multipath !== isMultipath || selectedRpc !== rpcUrl
    setSlippageTolerance(finalValue)
    setIsMultipath(multipath)
    if (selectedRpc !== rpcUrl) {
      setRpcUrl(selectedRpc)
      setIsDev(isTestNetwork(selectedRpc))
    }
    if (changed) toast.success(t('settings.title') + ' saved')
    onClose()
  }

  const rpcDisplay = rpcUrl.replace('https://', '').replace('http://', '')

  const renderLatency = (rpc: string) => {
    const ms = latencies[rpc]
    if (ms === undefined) return <span className="text-[11px] text-[#ccc]">...</span>
    if (ms === null) return <span className="text-[11px] text-[#e55]">timeout</span>
    const color = ms < 300 ? '#498846' : ms < 800 ? '#d4a017' : '#e55'
    return <span className="text-[11px] font-medium" style={{ color }}>{ms}ms</span>
  }

  const renderNodeList = (nodes: NetworkNode[]) =>
    nodes.map((node) => (
      <label
        key={node.rpc}
        className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-[#f3f3f3] last:border-b-0 hover:bg-[#f9f9f9] transition-colors ${selectedRpc === node.rpc ? 'bg-[#f0f7f0]' : ''}`}
      >
        <input
          type="radio"
          name="rpc-node"
          checked={selectedRpc === node.rpc}
          onChange={() => setSelectedRpc(node.rpc)}
          className="accent-[#498846] w-[16px] h-[16px] shrink-0"
        />
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-[13px] font-bold text-[#333]">{node.name}</span>
          <span className="text-[11px] text-[#919da6] truncate">{node.rpc}</span>
        </div>
        {renderLatency(node.rpc)}
      </label>
    ))

  return (
    <Modal open={open} onClose={handleClose} title={t('settings.title')} className="max-w-[450px] sm:max-w-[450px] p-6">
      {/* Slippage */}
      <div>
        <div className="text-[14px] font-bold text-[#091b3d] mb-3">{t('settings.slippage')}</div>
        <div className="flex flex-wrap gap-2">
          {SLIPPAGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setSelectedSlippage(opt.value); setIsCustom(false) }}
              className={`h-[32px] px-4 rounded-full border text-[14px] cursor-pointer transition-colors ${
                !isCustom && selectedSlippage === opt.value
                  ? 'bg-[#498846] text-white border-[#498846]'
                  : 'text-[#498846] border-[#d1e2ce] hover:border-[#498846]'
              }`}
            >
              {opt.label}
            </button>
          ))}
          <div className={`relative h-[32px] w-[120px] rounded-full border text-[14px] ${isCustom ? 'border-[#498846]' : 'border-[#d1e2ce]'}`}>
            <input
              type="number"
              value={customValue}
              placeholder={t('settings.custom')}
              onChange={(e) => { setCustomValue(e.target.value); setIsCustom(true) }}
              onFocus={() => setIsCustom(true)}
              className="absolute inset-0 w-full h-full rounded-full px-3 pr-6 text-center text-[14px] outline-none bg-transparent"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#333] text-[14px]">%</span>
          </div>
        </div>
      </div>

      {/* Multipath */}
      <div className="flex items-center justify-between mt-5">
        <span className="text-[14px] font-bold text-[#091b3d]">{t('settings.multipath')}</span>
        <button
          onClick={() => setMultipath(!multipath)}
          className={`relative w-[40px] h-[22px] rounded-full transition-colors cursor-pointer ${multipath ? 'bg-[#498846]' : 'bg-[#ccc]'}`}
        >
          <span className={`absolute top-[2px] h-[18px] w-[18px] rounded-full bg-white transition-transform ${multipath ? 'left-[20px]' : 'left-[2px]'}`} />
        </button>
      </div>

      {/* RPC Node */}
      <div className="mt-5">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setShowNodes(!showNodes)}
        >
          <span className="text-[14px] font-bold text-[#091b3d]">{t('settings.rpcNode')}</span>
          <span className="flex items-center gap-1 text-[12px] text-[#919da6]">
            {rpcDisplay}
            <ChevronRight className={`h-[14px] w-[14px] transition-transform ${showNodes ? 'rotate-90' : ''}`} />
          </span>
        </div>

        {showNodes && (
          <div className="mt-3 border border-[#f3f3f3] rounded-lg overflow-hidden">
            {renderNodeList(IS_DEV ? [...testNetworks, ...publicNetworks] : publicNetworks)}
          </div>
        )}
      </div>
    </Modal>
  )
}
